import React from 'react';
import { FlexDiv, TextDefault, TextInput } from './ui';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import {
    Checkbox,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    getBlockOptionComponent,
    getOptionActionKey,
    getScoreForBlockOption,
    removeBlockOptionScore,
    renameValueKeyInBlockAction,
    setBlockOptionComponent,
    setBlockValuesScore,
} from './questionActionsEditor';
import { ScoringComponentSelect } from './scoringComponentSelect';

const paperSx = {
    p: 2,
    mb: 2,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'grey.50',
    boxShadow: 1,
};

export function ConversationBlockEditor({
    selectedBlock,
    question,
    scoringComponentOptions,
    onPatchQuestion,
    onUpdateBlock,
}) {
    if (selectedBlock.type !== 'conversation') return null;

    const options = selectedBlock.options || [];
    const minOpts = 1;

    return (
        <FlexDiv column gap={2}>
            <TextDefault bold>Conversation layout</TextDefault>
            <TextInput
                label="Left avatar URL"
                value={selectedBlock.leftThumb || ''}
                onChange={(e) =>
                    onUpdateBlock({ leftThumb: e.target.value })
                }
                placeholder="/static/..."
            />
            <TextInput
                label="Right avatar URL"
                value={selectedBlock.rightThumb || ''}
                onChange={(e) =>
                    onUpdateBlock({ rightThumb: e.target.value })
                }
                placeholder="/static/..."
            />
            <TextDefault bold>Lines and scores</TextDefault>
            <TextDefault lighter>
                One card per line. Value is the score key in actions.
            </TextDefault>
            {options.map((row, index) => {
                const optKey = getOptionActionKey(row, index);
                const score = getScoreForBlockOption(
                    question.actions,
                    selectedBlock.actionId,
                    optKey
                );
                const lineComponent =
                    selectedBlock.actionId && onPatchQuestion
                        ? getBlockOptionComponent(
                              question.actions,
                              selectedBlock.actionId,
                              optKey,
                              question
                          )
                        : 'behaviour';
                const canRemove = options.length > minOpts;
                const side = row.side || 'left';
                return (
                    <Paper
                        key={`${selectedBlock.id}-conv-${index}`}
                        variant="outlined"
                        sx={paperSx}
                    >
                        <Stack spacing={2}>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <TextDefault bold>
                                    Line {index + 1}
                                </TextDefault>
                                <IconButton
                                    size="small"
                                    disabled={!canRemove}
                                    aria-label="Remove line"
                                    onClick={() => {
                                        if (!onPatchQuestion) return;
                                        onPatchQuestion((prev) => {
                                            const bid = selectedBlock.id;
                                            const block = prev.ui.blocks.find(
                                                (b) => b.id === bid
                                            );
                                            if (!block) return prev;
                                            const key = getOptionActionKey(
                                                block.options[index],
                                                index
                                            );
                                            const newOpts = (
                                                block.options || []
                                            ).filter((_, i) => i !== index);
                                            const nextActions =
                                                removeBlockOptionScore(
                                                    prev.actions || {},
                                                    block.actionId,
                                                    key
                                                );
                                            const blocks = prev.ui.blocks.map(
                                                (b) =>
                                                    b.id === bid
                                                        ? {
                                                              ...b,
                                                              options: newOpts,
                                                          }
                                                        : b
                                            );
                                            return {
                                                ...prev,
                                                ui: { ...prev.ui, blocks },
                                                actions: nextActions,
                                            };
                                        });
                                    }}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                            <FormControl size="small" fullWidth>
                                <InputLabel id={`conv-side-${index}`}>
                                    Side
                                </InputLabel>
                                <Select
                                    labelId={`conv-side-${index}`}
                                    label="Side"
                                    value={side}
                                    onChange={(e) => {
                                        const next = [...options];
                                        next[index] = {
                                            ...row,
                                            side: e.target.value,
                                        };
                                        onUpdateBlock({ options: next });
                                    }}
                                >
                                    <MenuItem value="left">left</MenuItem>
                                    <MenuItem value="right">right</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                size="small"
                                label="Value (score key)"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={
                                    row.value != null ? String(row.value) : ''
                                }
                                onChange={(e) => {
                                    if (!onPatchQuestion) {
                                        const next = [...options];
                                        next[index] = {
                                            ...row,
                                            value: e.target.value,
                                        };
                                        onUpdateBlock({ options: next });
                                        return;
                                    }
                                    const newVal = e.target.value;
                                    onPatchQuestion((prev) => {
                                        const bid = selectedBlock.id;
                                        const block = prev.ui.blocks.find(
                                            (b) => b.id === bid
                                        );
                                        if (!block?.actionId) return prev;
                                        const oldKey = getOptionActionKey(
                                            block.options[index],
                                            index
                                        );
                                        const newOpts = [
                                            ...(block.options || []),
                                        ];
                                        newOpts[index] = {
                                            ...block.options[index],
                                            value: newVal,
                                        };
                                        const newKey = getOptionActionKey(
                                            newOpts[index],
                                            index
                                        );
                                        let actions = prev.actions || {};
                                        if (
                                            oldKey !== newKey &&
                                            !String(oldKey).startsWith('__idx_')
                                        ) {
                                            actions = renameValueKeyInBlockAction(
                                                actions,
                                                block.actionId,
                                                oldKey,
                                                newKey
                                            );
                                        }
                                        const blocks = prev.ui.blocks.map(
                                            (b) =>
                                                b.id === bid
                                                    ? { ...b, options: newOpts }
                                                    : b
                                        );
                                        return {
                                            ...prev,
                                            ui: { ...prev.ui, blocks },
                                            actions,
                                        };
                                    });
                                }}
                            />
                            <TextField
                                size="small"
                                label="Score"
                                type="number"
                                fullWidth
                                sx={{ maxWidth: 200 }}
                                InputLabelProps={{ shrink: true }}
                                value={score}
                                inputProps={{ step: 'any' }}
                                onChange={(e) => {
                                    if (!onPatchQuestion || !selectedBlock.actionId) {
                                        return;
                                    }
                                    onPatchQuestion((prev) => {
                                        const bid = selectedBlock.id;
                                        const block = prev.ui.blocks.find(
                                            (b) => b.id === bid
                                        );
                                        if (!block?.actionId) return prev;
                                        const key = getOptionActionKey(
                                            block.options[index],
                                            index
                                        );
                                        return {
                                            ...prev,
                                            actions: setBlockValuesScore(
                                                prev.actions || {},
                                                block.actionId,
                                                key,
                                                e.target.value,
                                                prev
                                            ),
                                        };
                                    });
                                }}
                            />
                            {onPatchQuestion && selectedBlock.actionId ? (
                                <ScoringComponentSelect
                                    componentOptions={scoringComponentOptions}
                                    value={lineComponent}
                                    onChange={(comp) => {
                                        onPatchQuestion((prev) => {
                                            const bid = selectedBlock.id;
                                            const block = prev.ui.blocks.find(
                                                (b) => b.id === bid
                                            );
                                            if (!block?.actionId) return prev;
                                            const key = getOptionActionKey(
                                                block.options[index],
                                                index
                                            );
                                            return {
                                                ...prev,
                                                actions: setBlockOptionComponent(
                                                    prev.actions || {},
                                                    block.actionId,
                                                    key,
                                                    comp,
                                                    prev
                                                ),
                                            };
                                        });
                                    }}
                                />
                            ) : null}
                            <TextField
                                size="small"
                                label="Text (i18n key or copy)"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={
                                    row.text != null ? String(row.text) : ''
                                }
                                onChange={(e) => {
                                    const next = [...options];
                                    next[index] = {
                                        ...row,
                                        text: e.target.value,
                                    };
                                    onUpdateBlock({ options: next });
                                }}
                            />
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Checkbox
                                    checked={Boolean(row.isSelectNoneCheckbox)}
                                    onChange={(e) => {
                                        const next = [...options];
                                        next[index] = {
                                            ...row,
                                            isSelectNoneCheckbox:
                                                e.target.checked,
                                        };
                                        onUpdateBlock({ options: next });
                                    }}
                                />
                                <TextDefault lighter>
                                    Select-none option
                                </TextDefault>
                            </Stack>
                        </Stack>
                    </Paper>
                );
            })}
            {onPatchQuestion && selectedBlock.actionId ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <IconButton
                        size="small"
                        color="primary"
                        aria-label="Add line"
                        onClick={() => {
                            onPatchQuestion((prev) => {
                                const bid = selectedBlock.id;
                                const block = prev.ui.blocks.find(
                                    (b) => b.id === bid
                                );
                                if (!block?.actionId) return prev;
                                const slug = `line_${Date.now().toString(36)}`;
                                const nextId =
                                    (block.options || []).reduce(
                                        (m, o) =>
                                            Math.max(m, Number(o.id) || 0),
                                        0
                                    ) + 1;
                                const line = {
                                    id: nextId,
                                    side: 'left',
                                    text: '',
                                    value: slug,
                                };
                                const newOpts = [...(block.options || []), line];
                                const nextActions = setBlockValuesScore(
                                    prev.actions || {},
                                    block.actionId,
                                    slug,
                                    0,
                                    prev
                                );
                                const blocks = prev.ui.blocks.map((b) =>
                                    b.id === bid ? { ...b, options: newOpts } : b
                                );
                                return {
                                    ...prev,
                                    ui: { ...prev.ui, blocks },
                                    actions: nextActions,
                                };
                            });
                        }}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                    <TextDefault lighter>Add conversation line</TextDefault>
                </Stack>
            ) : null}
        </FlexDiv>
    );
}
